import type { LucideIcon } from 'lucide-react';
import type { AttentionCount, AttentionCounts } from '../counters/use-attention-counts';
import { isModuleVisible, type NavCapabilities } from '../navigation';
import {
  AUXILIARY_SCREENS,
  MODULE_CATALOG,
  type CatalogModule,
  type ModuleGate,
  type ShellTranslate,
} from './module-catalog';

/**
 * The catalog, gated and translated — a pure function, deliberately.
 *
 * Everything the shell renders derives from this one result: the rail's two levels, the bottom
 * bar, the breadcrumb, the command palette. Keeping it pure is what makes all four provably
 * consistent with each other and testable without mounting a single provider: every gate
 * combination is a plain object argument, so "does an external investigator see the
 * identity-documents entry" is a two-line test rather than four provider mocks.
 *
 * `useNavigationModel` is the thin hook that reads the live providers and calls this. The split is
 * the point: the decisions live here, the plumbing lives there.
 */

export type ResolvedSection = {
  readonly id: string;
  readonly label: string;
  readonly path: string;
  readonly end: boolean;
};

export type ResolvedModule = {
  readonly id: string;
  readonly label: string;
  readonly path: string;
  readonly basePath: string;
  readonly end: boolean;
  readonly icon: LucideIcon;
  readonly sections: readonly ResolvedSection[];
  /**
   * Sections of this module the caller's gates hide from the strips, kept only so the header can
   * still name a screen reached by address (never listed, never marked active).
   */
  readonly hiddenSections?: readonly ResolvedSection[];
  /** Present only when a live counter source reported something worth showing — see `withCounts`. */
  readonly count?: AttentionCount;
};

/** A screen that can be NAMED but never appears in navigation — today, `/profile` alone. */
export type ResolvedAuxiliaryScreen = {
  readonly id: string;
  readonly label: string;
  readonly basePath: string;
};

/**
 * Everything the gates need, flattened to plain data.
 *
 * `financeAllowed` is a boolean rather than the capability read's own three-state status on
 * purpose: `loading` and `denied` both mean "do not show the entry", and collapsing them here
 * keeps the resolver from having to know that the Finance capability is fetched at all. The rail
 * therefore GAINS the Finance entry when the read resolves, rather than flickering it away.
 */
export type NavigationGateState = {
  readonly isWorkspaceAccount: boolean;
  readonly isOperator: boolean;
  /** Holds at least one administrator grant (`/v1/administration/scopes` answered a non-empty list). */
  readonly isAdministrator: boolean;
  readonly financeAllowed: boolean;
  readonly capabilities: NavCapabilities;
  /**
   * Permission codes the caller is KNOWN to hold, from the modules that can report their own.
   *
   * Known is the operative word and it is why this is a plain set rather than a three-state. While
   * a read is in flight, and on any failure, the code is simply absent — so a `permission` gate
   * closes. That direction is deliberate and matches `canUse`, the predicate the clinical-study
   * pages already use: hiding an entry from someone entitled costs them a reload, whereas showing
   * one that cannot work costs them a red error page and the belief that the product is broken.
   */
  readonly heldPermissions: ReadonlySet<string>;
};

function isGateOpen(gate: ModuleGate, gates: NavigationGateState): boolean {
  switch (gate.kind) {
    case 'always':
      return true;
    case 'workspace-domain':
      return gates.isWorkspaceAccount;
    case 'finance-capability':
      return gates.financeAllowed;
    case 'operator':
      return gates.isOperator;
    case 'registry':
      return isModuleVisible(gate, gates.capabilities);
    case 'administrator':
      return gates.isAdministrator || gates.isOperator;
    case 'permission':
      return gate.anyOf.some((code) => gates.heldPermissions.has(code));
    default: {
      // Exhaustive by construction: a new gate kind fails to compile here until it is handled.
      const unhandled: never = gate;
      return unhandled;
    }
  }
}

/**
 * Sections that depend on where the caller is rather than on the catalog — today the
 * administration area's, which follow the place in the URL and what the caller may do there
 * (FR-AD-2). Keyed by module id; an entry REPLACES that module's static sections, after the
 * module's own gate has run, so it can never bring back a module the gates removed.
 */
export type DynamicSections = Readonly<Record<string, readonly ResolvedSection[]>>;

export function resolveModules(
  gates: NavigationGateState,
  translate: ShellTranslate,
  catalog: readonly CatalogModule[] = MODULE_CATALOG,
  dynamicSections: DynamicSections = {},
): readonly ResolvedModule[] {
  return catalog
    .filter((module) => isGateOpen(module.gate, gates))
    .map((module) => resolveModule(module, gates, translate, dynamicSections));
}

function resolveModule(
  module: CatalogModule,
  gates: NavigationGateState,
  translate: ShellTranslate,
  dynamicSections: DynamicSections,
): ResolvedModule {
  const resolveSection = (section: CatalogModule['sections'][number]): ResolvedSection => ({
    id: section.id,
    label: translate(section.labelKey),
    path: section.path,
    end: section.end ?? false,
  });
  // A section with no gate of its own inherits the module's verdict, which is how every
  // section behaved before Finance needed one gate per screen (Human decision 27).
  const open = (section: CatalogModule['sections'][number]) =>
    section.gate === undefined || isGateOpen(section.gate, gates);
  const dynamic = dynamicSections[module.id];
  const hidden = dynamic === undefined ? module.sections.filter((s) => !open(s)) : [];
  return {
    id: module.id,
    label: translate(module.labelKey),
    path: module.path,
    basePath: module.basePath,
    end: module.end ?? false,
    icon: module.icon,
    sections: dynamic ?? module.sections.filter(open).map(resolveSection),
    ...(hidden.length > 0 ? { hiddenSections: hidden.map(resolveSection) } : {}),
  };
}

/**
 * Attaches live counters to already-resolved modules — a second pure step rather than an argument
 * to `resolveModules`, deliberately.
 *
 * Gating and counting answer different questions from different sources. Gating is synchronous and
 * static: the catalog plus the caller's own capabilities, known before the first paint. A counter
 * is asynchronous and per-organization, arrives late, and changes on its own. Folding the two into
 * one function would make every counter arrival re-run every gate, and would make "which modules
 * does this account see" untestable without also stubbing a counter source. Composed instead, each
 * is a one-line test.
 *
 * A module with no entry in `counts` is returned UNCHANGED — not with `count: undefined`, which
 * `exactOptionalPropertyTypes` rejects and which would also be a different object identity on every
 * render.
 */
export function withCounts(
  modules: readonly ResolvedModule[],
  counts: AttentionCounts,
): readonly ResolvedModule[] {
  return modules.map((module) => {
    const count = counts[module.id];
    return count === undefined ? module : { ...module, count };
  });
}

export function resolveAuxiliaryScreens(
  translate: ShellTranslate,
): readonly ResolvedAuxiliaryScreen[] {
  return AUXILIARY_SCREENS.map((screen) => ({
    id: screen.id,
    label: translate(screen.labelKey),
    basePath: screen.basePath,
  }));
}
